import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Theme } from "@material-ui/core";
import { useAtomicStyles } from "styles";
import { useForm } from "react-hook-form";
import { actions, selectors } from "redux-store";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { FileManager } from "../../models";
import { useCurrentMerchantId, useTypedTranslations } from "hooks";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inn: {
      margin: "10px",
    },
    submitEventErrorAnimation: {
      animation: "$shake",
      animationDuration: "0.7s",
    },
    tabContent: {
      margin: "1em",
      padding: "10px",
    },
    submitEventButton: {
      marginTop: 0,
      marginLeft: "1em",
    },
    createButton: {
      margin: 0,
      width: "100%",
      height: "100%",
      position: "relative",
      right: "10px",
    },
  }),
);
const maxTitleLength = 30;
const maxContentLength = 200;

const schema = yup.object().shape({
  title: yup
    .string()
    .trim()
    .matches(/^(|.{3,30})$/, `Between 3-${maxTitleLength} characters`)
    .optional(),
  content: yup.string().trim().optional(),
  expiringMinutes: yup.number().optional(),
  imageUri: yup.string().optional(),
  expiringHours: yup.number(),
  expiringDays: yup.number(),
});

type DefaultEventFormData = {
  title: string;
  content: string;
  imageUri: string;
  expiringMinutes: number;
  expiringDays: number;
  expiringHours: number;
};

const useDefaultEventForm = () => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const classes = useMemo(() => ({}), []);
  const atomicStyles = useAtomicStyles();
  const {
    register,
    handleSubmit,
    errors,
    watch,
    control,
    setValue,
    trigger,
  } = useForm<DefaultEventFormData>({
    resolver: yupResolver(schema),
  });
  watch();

  const isLoading = useSelector(
    selectors.getAjaxIsLoadingByApi(actions.putMerchantsByMerchantId.api),
  );
  const [t] = useTypedTranslations();
  const dispatch = useDispatch();

  const merchantId = useCurrentMerchantId();

  const merchant = useSelector(selectors.getMerchantsList).find(
    (merch) => merch._id === merchantId,
  );

  useEffect(() => {
    dispatch(actions.getMerchants.request({ merchantId }));
  }, [dispatch, merchantId]);

  const [imageB64, setImageB64] = useState("");

  const onSubmit = useMemo(
    () =>
      handleSubmit((formData) => {
        const params = {
          merchantId,
          imageUri: undefined,
          defaultEvent: {
            imageUri: formData.imageUri,
            title: formData.title,
            content: formData.content,
            expiringMinutes:
              Number(formData.expiringMinutes) +
              Number(formData.expiringHours) * 60 +
              Number(formData.expiringDays) * 24 * 60,
          },
        };

        dispatch(actions.putMerchantsByMerchantId.request(params));
      }),
    [handleSubmit, merchantId, dispatch],
  );
  const handleDialogClose = useCallback(() => {
    dispatch(actions.setIsCreateDefaultEventDialogOpen(false));
  }, [dispatch]);
  const handleChangeImage = useCallback(
    (newB64: string) => {
      setValue("imageUri", newB64, { shouldValidate: true, shouldDirty: true });
      setImageB64(newB64);
    },
    [setValue],
  );
  const uploadLogo = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        setValue("imageUri", "", { shouldValidate: imageB64 !== "" });
        setImageB64("");
        return;
      }

      return FileManager.convertFileToBase64(files[0]).then(
        (pictureBase64: string) => {
          handleChangeImage(pictureBase64);
        },
      );
    },
    [handleChangeImage, imageB64, setValue],
  );
  const timeExpire = useMemo(
    () => ({
      hours: merchant.defaultEvent
        ? merchant.defaultEvent.expiringMinutes
          ? parseInt(
              Number(
                (Number(merchant.defaultEvent.expiringMinutes) % 1440) / 60,
              ).toString(),
            )
          : undefined
        : undefined,
      days: merchant.defaultEvent
        ? merchant.defaultEvent.expiringMinutes
          ? parseInt(
              Number(
                Number(merchant.defaultEvent.expiringMinutes) / 1440,
              ).toString(),
            )
          : undefined
        : undefined,
      minutes: merchant.defaultEvent
        ? merchant.defaultEvent.expiringMinutes
          ? parseInt(
              Number(
                Number(merchant.defaultEvent.expiringMinutes) % 60,
              ).toString(),
            )
          : undefined
        : undefined,
    }),
    [merchant],
  );

  return {
    styles,
    classes,
    atomicStyles,
    register,
    errors,
    onSubmit,
    uploadLogo,
    maxTitleLength,
    maxContentLength,
    handleDialogClose,
    imageB64,
    isLoading,
    watch,
    merchant,
    timeExpire,
    t,
  };
};

export { useDefaultEventForm as default };
